import React, { useMemo } from "react";
import { formatMoney } from "../../../../features/utils/formatMoney";
import {
  getChangeAdminGuidance,
  getChangeKindLabel,
  hasChangeQuote,
  isChangePaymentSecured,
} from "../../../../data/flight/order/getChangeAdminGuidance";

const TRAVELLER_LIB = {
  ADT: "Adult",
  CHD: "Child",
  CNN: "Child",
  INF: "Infant",
  INS: "Infant",
};

/**
 * Task 5 + confirmation view — internal pricing lines for staff.
 */
function ChangeDateConfirmationPriceBreakdown({ data }) {
  const guidance = getChangeAdminGuidance(data);
  const quoted = hasChangeQuote(data);
  const emptyQuote =
    !quoted &&
    [
      "needs_review",
      "auto_processing",
      "needs_quote",
      "validation_failed",
    ].includes(guidance.stage);

  const passengers = data?.passengerInfo || [];
  const raw = data?.priceBreakdown || [];
  const currency =
    data?.currency ||
    data?.flightBooking?.[0]?.currency ||
    data?.flightBooking?.currency ||
    "";

  const cachedAdmin = useMemo(() => {
    try {
      return JSON.parse(
        sessionStorage.getItem(`changeQuoteAdmin:${data?._id}`) || "null",
      );
    } catch (_) {
      return null;
    }
  }, [data?._id]);

  const perPassenger = useMemo(() => {
    return raw.map((it) => {
      const p = passengers.find((pp) => pp?.id === it?.passengerId) || {};
      const travellerType =
        it?.travellerType || TRAVELLER_LIB[p?.travelerType] || "Adult";
      const name =
        it?.passenger ||
        [p?.firstName, p?.middleName, p?.lastName].filter(Boolean).join(" ") ||
        travellerType;

      const cacheRow = (cachedAdmin?.priceBreakdown || []).find(
        (c) => String(c?.passengerId) === String(it?.passengerId),
      );

      const linesSubtotal =
        (it.changePrice || 0) +
        (it.ticketDifference || 0) +
        (it.penaltyFee || 0) +
        (it.taxDifference || 0) +
        (it.supplierFee || 0);

      return {
        ...it,
        travellerType,
        passenger: name,
        linesSubtotal,
        airlineChange:
          it.airlineChangePrice ?? cacheRow?.airlineChangePrice ?? null,
        airlineFare:
          it.airlineFareDifference ??
          cacheRow?.airlineFareDifference ??
          data?.airlineFareDifference ??
          null,
        fareMarkup:
          it.fareDifferenceMarkup ??
          cacheRow?.fareDifferenceMarkup ??
          data?.fareDifferenceMarkup ??
          null,
        airlineTax:
          it.airlineTaxDifference ??
          cacheRow?.airlineTaxDifference ??
          data?.airlineTaxDifference ??
          null,
        taxDifference:
          it.taxDifference ?? cacheRow?.taxDifference ?? data?.taxDifference ?? 0,
        airlineSupplierFee:
          it.airlineSupplierFee ??
          cacheRow?.airlineSupplierFee ??
          data?.airlineSupplierFee ??
          null,
        supplierFee:
          it.supplierFee ?? cacheRow?.supplierFee ?? data?.supplierFee ?? 0,
      };
    });
  }, [raw, passengers, data, cachedAdmin]);

  const supplierFareDiff = Number(
    data?.supplierChangePrice || cachedAdmin?.airlineFareDifference || 0,
  );
  const serviceFee = Number(
    data?.intraverseServiceFee ||
      data?.changeFee ||
      cachedAdmin?.intraverseServiceFee ||
      0,
  );
  const totalPayable = Number(
    data?.paymentAmount || data?.payable || data?.totalAmountPayable || 0,
  );
  const displayCurrency = currency || cachedAdmin?.currency || "";

  if (emptyQuote) {
    return (
      <div className="w-full border h-fit border-gray-200 rounded-xl overflow-hidden bg-white mt-4 p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">
            {getChangeKindLabel(data)}
          </h2>
        </div>
        <div className="bg-primary1/5 p-4 text-sm text-gray-700 gap-2 flex flex-col">
          <div className="bg-[#FE9F29]/10 w-full rounded-md min-h-9 px-3 flex items-center justify-center text-[#FE9F29] font-semibold text-sm text-center">
            {guidance.statusLabel}
          </div>
          <span className="text-xs text-gray-600 text-center">
            {guidance.description}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full border h-fit border-gray-200 rounded-xl overflow-hidden bg-white mt-4 p-4">
      <div className="flex items-center justify-between mb-4 gap-2">
        <h2 className="text-lg font-bold text-gray-900">
          {getChangeKindLabel(data)}
        </h2>
        <StatusBadge data={data} />
      </div>
      {displayCurrency ? (
        <div className="text-xs text-gray-500 mb-2">
          Currency: <span className="font-semibold text-gray-800">{displayCurrency}</span>
        </div>
      ) : null}
      {(guidance.stage === "needs_airline_submit" ||
        guidance.stage === "send_failed" ||
        guidance.stage === "needs_reissue" ||
        guidance.stage === "settlement_failed") && (
        <p className="text-xs text-gray-600 mb-3">{guidance.description}</p>
      )}

      <div className="bg-primary1/5 p-4 sm:p-6 text-sm text-gray-700">
        {supplierFareDiff > 0 ? (
          <div className="flex justify-between items-center mb-3 rounded-lg bg-white border border-[#E8EEF5] px-3 py-2">
            <div>
              <div className="text-xs font-medium text-[#6D7C94]">
                Supplier fare difference
              </div>
              <div className="text-[10px] text-[#94A3B8]">
                Airline / supplier cost
              </div>
            </div>
            <span className="font-semibold text-[#0F172A]">
              {formatMoney(supplierFareDiff)}
            </span>
          </div>
        ) : null}

        {perPassenger.map((row) => (
          <div key={row._id || row.passengerId} className="mb-4 last:mb-0">
            <div className="flex justify-between items-center mb-2">
              <span className="font-medium">1 × {row.travellerType}</span>
              <span className="font-semibold text-gray-900">
                {formatMoney(row.linesSubtotal)}
              </span>
            </div>

            <div className="pl-4 space-y-1">
              {row.airlineChange != null && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Airline change fee</span>
                  <span className="text-gray-600">
                    {formatMoney(row.airlineChange || 0)}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">Change fee (agent pays)</span>
                <span className="text-gray-700">
                  {formatMoney(row.changePrice || 0)}
                </span>
              </div>

              {row.airlineFare != null && Number(row.airlineFare) > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Airline fare difference</span>
                  <span className="text-gray-600">
                    {formatMoney(row.airlineFare || 0)}
                  </span>
                </div>
              )}
              {row.fareMarkup != null && Number(row.fareMarkup) !== 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Intraverse markup</span>
                  <span className="text-emerald-700">
                    {formatMoney(row.fareMarkup || 0)}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">
                  Selling fare difference
                </span>
                <span className="text-gray-700">
                  {formatMoney(row.ticketDifference || 0)}
                </span>
              </div>

              {(row.penaltyFee || 0) > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Airline penalty</span>
                  <span className="text-gray-700">
                    {formatMoney(row.penaltyFee || 0)}
                  </span>
                </div>
              )}

              {(Number(row.airlineTax) > 0 || Number(row.taxDifference) > 0) && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Tax difference</span>
                  <span className="text-gray-700">
                    {formatMoney(row.taxDifference || row.airlineTax || 0)}
                  </span>
                </div>
              )}

              {(Number(row.airlineSupplierFee) > 0 ||
                Number(row.supplierFee) > 0) && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Supplier fee</span>
                  <span className="text-gray-700">
                    {formatMoney(row.supplierFee || row.airlineSupplierFee || 0)}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}

        <div className="flex justify-between items-center mt-4 mb-2 rounded-lg bg-[#FFFBEB] border border-[#FEF3C7] px-3 py-2">
          <div>
            <span className="font-medium text-[#92400E]">Service fee</span>
            <div className="text-[10px] text-[#B45309]">
              Intraverse service fee
            </div>
          </div>
          <span className="font-semibold text-[#92400E]">
            {formatMoney(serviceFee)}
          </span>
        </div>

        <hr className="border-gray-300 mb-3" />

        <div className="flex justify-between items-center mb-1">
          <span className="font-semibold text-gray-900">Total</span>
          <span className="font-semibold text-gray-900">
            {formatMoney(totalPayable)}
            {displayCurrency ? (
              <span className="text-xs font-normal text-gray-500 ml-1">
                {displayCurrency}
              </span>
            ) : null}
          </span>
        </div>

        {isChangePaymentSecured(data) ? (
          <div className="text-[11px] text-emerald-700 mt-1">
            Payment secured — do not collect again.
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default ChangeDateConfirmationPriceBreakdown;

const StatusBadge = ({ data }) => {
  const { statusLabel, tone } = getChangeAdminGuidance(data);
  const className =
    tone === "success"
      ? "bg-green-100 text-green-700"
      : tone === "danger"
        ? "bg-red-100 text-red-700"
        : tone === "wait"
          ? "bg-[#1E61DC]/10 text-[#1E61DC]"
          : "bg-[#FE9F29]/10 text-[#FE9F29]";

  return (
    <span className={`${className} text-sm font-semibold px-3 py-1 rounded-md`}>
      {statusLabel}
    </span>
  );
};
